import type {
  CustomValidationRules,
  ValidationSchema,
} from '@wix/editor-elements-corvid-utils';
import {
  assert,
  messages,
  reportError,
} from '@wix/editor-elements-corvid-utils';
import type { CorvidSDKApi } from '@wix/editor-elements-types/corvid';
import type { ISingleTabSDK } from '../../SingleTab/SingleTab.types';
import {
  COMP_NAME,
  SINGLE_TAB_REFERENCE_PROPERTY,
  TAB_REFERENCES_PROPERTY,
} from '../constants';
import type { ITabsProps, ITabsSDK } from '../Tabs.types';

const checkTabExists = (
  tabs: Array<ISingleTabSDK>,
  tabReference: string | ISingleTabSDK,
) => {
  const tabId = assert.isString(tabReference) ? tabReference : tabReference.id;
  return tabs.some(({ id }) => id === tabId);
};

export const stringifyTabReference = (tabReference: string | ISingleTabSDK) => {
  return JSON.stringify(
    assert.isObject(tabReference) && tabReference.hasOwnProperty('toJSON')
      ? tabReference.toJSON()
      : tabReference,
  );
};

const isValidTabReference =
  (functionName: string) =>
  (
    functionArgs: [string | ISingleTabSDK],
    sdkFactoryArgs: CorvidSDKApi<ITabsProps>,
  ) => {
    const [tabReference] = functionArgs;
    const { getChildren } = sdkFactoryArgs;
    const tabs = getChildren();

    const tabExists = checkTabExists(tabs, tabReference);

    if (!tabExists) {
      reportError(
        messages.nonExistingItem({
          propertyName: SINGLE_TAB_REFERENCE_PROPERTY,
          functionName,
          value: stringifyTabReference(tabReference),
        }),
      );
    }

    return tabExists;
  };

export const isValidTabReferenceArray =
  (functionName: string) =>
  (
    functionArgs: [Array<string> | Array<ISingleTabSDK>],
    sdkFactoryArgs: CorvidSDKApi<ITabsProps>,
  ) => {
    const [tabReferences] = functionArgs;
    const { getChildren } = sdkFactoryArgs;
    const tabs = getChildren();

    if (assert.isNil(tabReferences)) {
      reportError(
        messages.nilAssignmentMessage({
          compName: COMP_NAME,
          propertyName: TAB_REFERENCES_PROPERTY,
          functionName,
        }),
      );
      return false;
    }

    if (tabs.length !== tabReferences.length) {
      reportError(
        messages.invalidArrayLength({
          propertyName: TAB_REFERENCES_PROPERTY,
          functionName,
          value: `[${tabReferences.map(stringifyTabReference).join(', ')}]`,
          arrayLength: tabs.length,
        }),
      );
      return false;
    }

    for (const tabReference of tabReferences) {
      const tabExists = checkTabExists(tabs, tabReference);

      if (!tabExists) {
        reportError(
          messages.nonExistingItem({
            propertyName: TAB_REFERENCES_PROPERTY,
            functionName,
            value: stringifyTabReference(tabReference),
          }),
        );
        return false;
      }
    }

    return true;
  };

export const customValidators: CustomValidationRules<ITabsProps, ITabsSDK> = {
  orderTabs: [isValidTabReferenceArray('orderTabs')],
  changeTab: [isValidTabReference('changeTab')],
};

export const validationSchema: ValidationSchema = {
  type: ['object'],
  properties: {
    orderTabs: {
      type: ['function'],
      args: [
        {
          type: ['array'],
          items: {
            type: ['object', 'string'],
          },
        },
      ],
    },
    changeTab: {
      type: ['function'],
      args: [
        {
          type: ['object', 'string'],
        },
      ],
    },
  },
};
